/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useRef, useEffect, useState, Fragment } from 'react';
import {
  dropTargetForElements,
  draggable,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';

interface DraggableTableRowProps {
  folder: any;
  level: number;
  isLastChild: boolean;
  parentChain: boolean[];
  onNest: (draggedId: number, targetId: number) => void;
  onRemove: (folderId: number) => void;
  canNest: (draggedId: number, targetId: number) => boolean;
  children?: any[];
}

export const DraggableTableRow: React.FC<DraggableTableRowProps> = ({
  folder,
  level,
  isLastChild,
  parentChain,
  onNest,
  onRemove,
  canNest,
  children,
}) => {
  const styles = useStyles(styleFn);
  const ref = useRef<HTMLTableRowElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isDropTarget, setIsDropTarget] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    return combine(
      draggable({
        element: el,
        getInitialData: () => ({ folderId: folder.id, level }),
        onDragStart: () => setIsDragging(true),
        onDrop: () => setIsDragging(false),
      }),
      dropTargetForElements({
        element: el,
        canDrop: ({ source }) => {
          const draggedId = source.data.folderId as number;
          return canNest(draggedId, folder.id);
        },
        onDragEnter: () => setIsDropTarget(true),
        onDragLeave: () => setIsDropTarget(false),
        onDrop: ({ source }) => {
          setIsDropTarget(false);
          const draggedId = source.data.folderId as number;
          onNest(draggedId, folder.id);
        },
      })
    );
  }, [folder, canNest, onNest, level]);

  const rowClass = `draggable-row ${isDragging ? 'dragging' : ''} ${
    isDropTarget ? 'drop-target' : ''
  }`;

  return (
    <Fragment>
      <tr ref={ref} className={rowClass}>
        <td>
          <div css={styles.cellContent}>
            {/* Tree lines container */}
            <div css={styles.treeLines}>
              {level > 0 && (
                <Fragment>
                  {/* Render vertical lines for each ancestor level */}
                  {parentChain.map((isLast, idx) => (
                    <div
                      key={idx}
                      css={[
                        styles.treeLine,
                        !isLast && styles.treeLineVertical,
                      ]}
                    />
                  ))}
                  {/* Current item's connector */}
                  <div
                    css={[
                      styles.treeLine,
                      isLastChild ? styles.treeLineL : styles.treeLineT,
                    ]}
                  />
                </Fragment>
              )}
            </div>

            {/* Existing content */}
            <FontAwesomeIcon
              icon={['fas', 'grip-vertical']}
              className="drag-handle"
            />
            <FontAwesomeIcon icon={['fas', 'folder']} css={styles.folderIcon} />
            <span css={styles.folderName}>{folder.name}</span>
          </div>
        </td>
        <td>
          <span css={styles.description}>{folder.description || ''}</span>
        </td>
        <td style={{ textAlign: 'center' }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove(folder.id);
            }}
            css={styles.removeButton}>
            <FontAwesomeIcon icon={['far', 'times']} />
          </button>
        </td>
      </tr>
      {children &&
        children.map((child, index) => (
          <DraggableTableRow
            key={child.id}
            folder={child}
            level={level + 1}
            isLastChild={index === children.length - 1}
            parentChain={level === 0 ? [] : [...parentChain, isLastChild]}
            onNest={onNest}
            onRemove={onRemove}
            canNest={canNest}
            children={child.children}
          />
        ))}
    </Fragment>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  cellContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minHeight: 40,
  },
  treeLines: {
    display: 'flex',
    alignItems: 'stretch',
    alignSelf: 'stretch',
    flexShrink: 0,
  },
  treeLine: {
    width: 24,
    position: 'relative',
    flexShrink: 0,
  },
  treeLineVertical: {
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 11,
      top: '-4px',
      bottom: '-4px',
      width: '2px !important',
      backgroundColor: '#8c8c8c !important',
      opacity: 0.6,
    },
  },
  treeLineT: {
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 11,
      top: '-4px',
      bottom: '-4px',
      width: '2px !important',
      backgroundColor: '#8c8c8c !important',
      opacity: 0.6,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 13,
      top: '50%',
      width: '12px !important',
      height: '2px !important',
      backgroundColor: '#8c8c8c !important',
      opacity: 0.6,
      marginTop: '-1px',
    },
  },
  treeLineL: {
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 11,
      top: '-4px',
      height: 'calc(50% + 4px)',
      width: '2px !important',
      backgroundColor: '#8c8c8c !important',
      opacity: 0.6,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 13,
      top: '50%',
      width: '12px !important',
      height: '2px !important',
      backgroundColor: '#8c8c8c !important',
      opacity: 0.6,
      marginTop: '-1px',
    },
  },
  folderIcon: {
    fontSize: 16,
    color: theme.colors.warning,
    flexShrink: 0,
  },
  folderName: {
    flex: 1,
    fontSize: 14,
    fontWeight: 500,
    color: theme.colors.text.primary,
  },
  description: {
    color: theme.colors.textSecondary,
    fontSize: 14,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
  },
  removeButton: {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    padding: '4px 8px',
    height: '24px',
    minWidth: 'auto',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    transition: 'all 0.2s ease',
    fontSize: '14px',
    color: theme.colors.textSecondary || '#8c8c8c',
    '&:hover': {
      backgroundColor: 'rgba(255, 77, 79, 0.1)',
      color: '#ff4d4f',
    },
    '&:active': {
      transform: 'scale(0.95)',
    },
    '& svg': {
      fontSize: '14px',
      color: 'inherit',
    },
  },
});
